import { useNextRouterContext } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { useSDUIFilteredComponentsRequest } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useSDUIFilteredComponentsRequest";
import { useSDUIFilterablePaginatedSectionState } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/useSDUIFilterablePaginatedSectionState";
import { useQuery as useFilteredComponentsQueryV3 } from "@faire/web-api--source/endpoints/www/api/v3/layout/filtered-components/post-hooks";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import { IGetSDUIFilteredComponentsRequest } from "@faire/web-api--source/indigofair/data/IGetSDUIFilteredComponentsRequest";
import { IPaginationData } from "@faire/web-api--source/indigofair/data/IPaginationData";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { useRefFrom } from "@faire/web/common/hooks/useRefFrom";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { keepPreviousData } from "@tanstack/react-query";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { startTransition, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

export const SDUIFilterablePaginatedSectionNextPagePrefetcher = ({
  filterablePaginatedSection,
  paginationData,
  handlePaginationLink,
  isRSC,
}: {
  filterablePaginatedSection: IFilterablePaginatedSection | undefined;
  paginationData: IPaginationData | undefined;
  handlePaginationLink: (pageNumber: number) => string;
  isRSC?: boolean;
}) => {
  if (!filterablePaginatedSection) {
    return null;
  }
  return (
    <SDUIFilterablePaginatedSectionNextPagePrefetcherWithReactQueryOrRSC
      filterablePaginatedSection={filterablePaginatedSection}
      paginationData={paginationData}
      handlePaginationLink={handlePaginationLink}
      isRSC={isRSC}
    />
  );
};

const SDUIFilterablePaginatedSectionNextPagePrefetcherWithReactQueryOrRSC = ({
  filterablePaginatedSection,
  paginationData,
  handlePaginationLink,
  isRSC,
}: {
  filterablePaginatedSection: IFilterablePaginatedSection;
  paginationData: IPaginationData | undefined;
  handlePaginationLink: (pageNumber: number) => string;
  isRSC?: boolean;
}) => {
  const SDUIFilteredComponentsRequest = useSDUIFilteredComponentsRequest(
    filterablePaginatedSection
  );
  const { pathname, search } = useLocation();
  const currentPath = `${pathname}${search}`;
  const [prefetchPagePath, setPrefetchPagePath] = React.useState(currentPath);
  const pageSize =
    SDUIFilteredComponentsRequest.pagination_data?.page_size ?? 0;
  const pageCount = paginationData?.page_count ?? 0;
  const { oneIndexedPageNumber } =
    useSDUIFilterablePaginatedSectionState(!!isRSC);

  // give time for page to scroll to top after page change
  const debouncedSetPrefetchPagePath = useRefFrom(() =>
    debounceIfPossible((path: string) => {
      startTransition(() => {
        setPrefetchPagePath(path);
      });
    }, 1000)
  ).current;

  useEffect(() => {
    if (prefetchPagePath !== currentPath) {
      debouncedSetPrefetchPagePath(currentPath);
    }
  }, [prefetchPagePath, currentPath, debouncedSetPrefetchPagePath]);

  const nextPageNumber = oneIndexedPageNumber + 1;
  const isNextPageAvailable = nextPageNumber <= pageCount && nextPageNumber > 0;
  const prefetchNextPageRequestPayload = useMemo(
    () =>
      IGetSDUIFilteredComponentsRequest.build({
        ...SDUIFilteredComponentsRequest,
        pagination_data: IPaginationData.build({
          page_size: pageSize,
          page_number: nextPageNumber,
        }),
      }),
    [SDUIFilteredComponentsRequest, nextPageNumber, pageSize]
  );

  if (!isNextPageAvailable || prefetchPagePath !== currentPath) {
    return null;
  }

  return (
    <WhenVisibilityIsImminent useTransition threshold={0}>
      <FilteredComponentsPrefetcher
        prefetchNextPageRequestPayload={prefetchNextPageRequestPayload}
        prefetchedPageLink={handlePaginationLink(nextPageNumber)}
        isRSC={isRSC}
      />
    </WhenVisibilityIsImminent>
  );
};

const FilteredComponentsPrefetcher = ({
  prefetchNextPageRequestPayload,
  prefetchedPageLink,
  isRSC,
}: {
  prefetchNextPageRequestPayload: IGetSDUIFilteredComponentsRequest;
  prefetchedPageLink: string;
  isRSC?: boolean;
}) => {
  const { prefetch: nextPrefetch } = useNextRouterContext();

  useFilteredComponentsQueryV3(prefetchNextPageRequestPayload, {
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    enabled: !isRSC,
  });

  if (isRSC) {
    nextPrefetch(prefetchedPageLink, {
      kind: PrefetchKind.FULL,
    });
  }

  return null;
};
